import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { applicationsAPI } from '../services/api';
import ConfirmModal from '../components/ConfirmModal';
import toast from 'react-hot-toast';
import { 
  Briefcase, 
  MapPin, 
  Clock, 
  Eye,
  XCircle,
  Filter
} from 'lucide-react';
import { Card, Badge, Button, Select } from '../components/ui';

const MyApplications: React.FC = () => {
  const [applications, setApplications] = useState<any[]>([]);
  const [stats, setStats] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'danger' | 'warning' | 'info' | 'success';
    confirmText?: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'danger',
    onConfirm: () => {}
  });
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchApplications();
  }, [filter]);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const params = filter ? { status: filter } : {};
      const response = await applicationsAPI.getMyApplications(params);
      setApplications(response.data.data || []);
      setStats(response.data.stats || {});
    } catch (error) {
      console.error('Error fetching applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleWithdraw = (id: string) => {
    setConfirmModal({
      isOpen: true,
      title: 'Withdraw Application',
      message: 'Are you sure you want to withdraw this application? This action cannot be undone.',
      type: 'warning',
      confirmText: 'Withdraw',
      onConfirm: async () => {
        setProcessing(true);
        try {
          await applicationsAPI.withdraw(id);
          toast.success('Application withdrawn');
          setConfirmModal({ ...confirmModal, isOpen: false });
          fetchApplications();
        } catch (error: any) {
          toast.error('Failed to withdraw application');
        } finally {
          setProcessing(false);
        }
      }
    });
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'submitted': return 'info';
      case 'reviewing': return 'warning';
      case 'shortlisted': return 'success';
      case 'interviewed': return 'primary';
      case 'offered': return 'success';
      case 'hired': return 'success';
      case 'rejected': return 'danger';
      default: return 'default';
    }
  };

  const filterOptions = [
    { value: '', label: 'All Applications' },
    { value: 'submitted', label: 'Submitted' },
    { value: 'reviewing', label: 'Reviewing' },
    { value: 'shortlisted', label: 'Shortlisted' },
    { value: 'interviewed', label: 'Interviewed' },
    { value: 'offered', label: 'Offered' },
    { value: 'rejected', label: 'Rejected' },
  ];

  return (
    <div className="min-h-screen bg-secondary-50 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-secondary-900">My Applications</h1>
          <p className="text-secondary-500 mt-1">Track all your job applications</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="p-4 text-center">
            <p className="text-2xl font-bold text-secondary-900">{stats.total || 0}</p>
            <p className="text-sm text-secondary-500">Total</p>
          </Card>
          <Card className="p-4 text-center">
            <p className="text-2xl font-bold text-warning-600">{stats.reviewing || 0}</p>
            <p className="text-sm text-secondary-500">Reviewing</p>
          </Card>
          <Card className="p-4 text-center">
            <p className="text-2xl font-bold text-success-600">{stats.shortlisted || 0}</p>
            <p className="text-sm text-secondary-500">Shortlisted</p>
          </Card>
          <Card className="p-4 text-center">
            <p className="text-2xl font-bold text-danger-600">{stats.rejected || 0}</p>
            <p className="text-sm text-secondary-500">Rejected</p>
          </Card>
        </div>

        {/* Filter */}
        <Card className="mb-6 p-4">
          <div className="flex items-center gap-4">
            <Filter className="w-5 h-5 text-secondary-400" />
            <Select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              options={filterOptions}
              placeholder="Filter by status"
            />
          </div>
        </Card>

        {/* Applications List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : applications.length > 0 ? (
          <div className="space-y-4">
            {applications.map((app) => (
              <Card key={app._id} className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                      <Briefcase className="w-6 h-6 text-primary-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-secondary-900">{app.job?.title}</h3>
                      <p className="text-secondary-600">{app.job?.business?.companyName || 'Company'}</p>
                      <p className="text-sm text-secondary-500 mt-1 flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {app.job?.location?.city}, {app.job?.location?.country}
                      </p>
                    </div>
                  </div>
                  <Badge variant={getStatusBadgeVariant(app.status)} size="md">
                    {app.status}
                  </Badge>
                </div>

                <div className="border-t border-secondary-100 pt-4">
                  <p className="text-sm text-secondary-600 mb-3 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Applied: {new Date(app.createdAt).toLocaleDateString()}
                  </p>
                  <div className="flex gap-4">
                    <Link
                      to={`/jobs/${app.job?._id}`}
                      className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
                    >
                      <Eye className="w-4 h-4" />
                      View Job
                    </Link>
                    {app.status === 'submitted' && (
                      <button
                        onClick={() => handleWithdraw(app._id)}
                        className="flex items-center gap-1 text-sm text-danger-600 hover:text-danger-700"
                      >
                        <XCircle className="w-4 h-4" />
                        Withdraw
                      </button>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="p-12 text-center">
            <p className="text-secondary-500 text-lg mb-4">No applications found</p>
            <Link to="/jobs">
              <Button variant="primary">Browse Jobs</Button>
            </Link>
          </Card>
        )}
      </div>

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        confirmText={confirmModal.confirmText}
        loading={processing}
      />
    </div>
  );
};

export default MyApplications;
